import { Component, ContentChild, ElementRef, forwardRef, input, output, signal, TemplateRef, ViewChild } from "@angular/core";
import { NgTemplateOutlet, TitleCasePipe } from "@angular/common";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { SelectChangeEvent, SelectFilterEvent, Select, SelectModule } from "primeng/select";

/** Context passed to projected `#selectOptionItem` templates. */
export interface SelectFieldOptionContext {
  $implicit: unknown;
  option: unknown;
  disabled: boolean;
  label: string;
}

/**
 * Standardized dropdown.
 *
 * `ControlValueAccessor` wrapper over PrimeNG `p-select` that defaults to
 * full-width, consistent placeholder/clear behavior and shared invalid styling.
 * Options must be supplied by the caller (from a domain service) — this
 * component never queries data itself.
 *
 * Optional projected template (direct child of `app-select-field`):
 * - `#selectOptionItem` — dropdown row; use `let-option` / `let-disabled="disabled"`.
 *
 * When `optionDisabled` is set, disabled options stay selectable in the panel visually (lock UI)
 * but selection is reverted and `disabledOptionClick` is emitted (PrimeNG native disabling blocks taps).
 */
@Component({
  selector: "app-select-field",
  standalone: true,
  imports: [SelectModule, FormsModule, NgTemplateOutlet],
  templateUrl: "./select-field.component.html",
  styleUrl: "./select-field.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFieldComponent),
      multi: true
    }
  ]
})
export class SelectFieldComponent implements ControlValueAccessor {
  /**
   * PrimeNG disables options when `optionDisabled` is unset but `option.disabled` exists.
   * Locked BQ rows must stay clickable for `disabledOptionClick`; this field name is never on options.
   */
  private static readonly PRIME_NG_OPTION_DISABLED_SENTINEL = "__appSelectFieldIgnoreDisabled__";

  @ContentChild("selectOptionItem") selectOptionItemTemplate?: TemplateRef<SelectFieldOptionContext>;
  @ViewChild(Select) private select?: Select;

  inputId = input<string>("");
  options = input<unknown[]>([]);
  optionLabel = input<string>("label");
  optionValue = input<string | undefined>(undefined);
  optionDisabled = input<string | undefined>(undefined);
  placeholder = input<string>("Select");
  showClear = input<boolean>(false);
  /** Enable the in-panel search box. */
  filter = input<boolean>(false);
  /** Option property the filter matches against. */
  filterBy = input<string | undefined>(undefined);
  filterPlaceholder = input<string>("Search");
  resetFilterOnHide = input<boolean>(true);
  /** Focus the filter input when the overlay opens. Defaults to true (PrimeNG default). */
  autoFocusFilter = input<boolean>(true);
  /** Style class applied to the overlay panel (e.g. `vendor-select-overlay`). */
  panelStyleClass = input<string>("");
  /** Replaces the "no results" panel content when a filter returns nothing. */
  emptyFilterTemplate = input<TemplateRef<unknown> | undefined>(undefined);
  /** Force invalid styling when used without `app-form-field`. */
  invalid = input<boolean>(false);
  testId = input<string>("app-select-field");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appendTo = input<HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any>(undefined);

  /** Emitted with the trimmed filter text as the user searches. */
  readonly filterChange = output<string>();
  readonly disabledOptionClick = output<unknown>();

  protected readonly value = signal<unknown>(null);
  protected readonly disabled = signal<boolean>(false);

  private onChange: (value: unknown) => void = () => { /* noop until registered */ };
  private onTouched: () => void = () => { /* noop until registered */ };
  private readonly titleCasePipe = new TitleCasePipe();
  /** Last value committed to the form; used to revert locked-option selection attempts. */
  private committedValue: unknown = null;

  writeValue(value: unknown): void {
    this.committedValue = value ?? null;
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected handleFilter(event: SelectFilterEvent): void {
    this.filterChange.emit((event.filter ?? "").trim());
  }

  /**
   * Display an option's label in title case (each word capitalized).
   * Display-only — the underlying option value is untouched.
   */
  protected displayLabel(option: unknown): string {
    const key = this.optionLabel();
    const raw =
      option !== null && typeof option === "object" && key in (option as Record<string, unknown>)
        ? (option as Record<string, unknown>)[key]
        : option;
    const text = String(raw ?? "");
    return this.titleCasePipe.transform(text);
  }

  protected isOptionDisabled(option: unknown): boolean {
    const resolved = this.resolveOption(option);
    const key = this.optionDisabled();
    if (!key || resolved === null || typeof resolved !== "object") {
      return false;
    }
    return !!(resolved as Record<string, unknown>)[key];
  }

  protected optionContext(option: unknown): SelectFieldOptionContext {
    const resolved = this.resolveOption(option);
    return {
      $implicit: resolved,
      option: resolved,
      disabled: this.isOptionDisabled(resolved),
      label: this.displayLabel(resolved)
    };
  }

  /** PrimeNG `optionDisabled` input — avoids native disable when options carry a `disabled` flag. */
  protected primeNgOptionDisabledField(): string | undefined {
    return this.optionDisabled()
      ? SelectFieldComponent.PRIME_NG_OPTION_DISABLED_SENTINEL
      : undefined;
  }

  protected handleDisabledOptionActivate(event: Event, option: unknown): void {
    const resolved = this.resolveOption(option);
    if (!this.isOptionDisabled(resolved)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.emitDisabledOptionClick(resolved);
  }

  protected handleChange(event: SelectChangeEvent): void {
    const selectedOption = this.findOptionByValue(event.value);
    if (selectedOption && this.isOptionDisabled(selectedOption)) {
      this.emitDisabledOptionClick(selectedOption);
      this.revertToCommittedValue();
      return;
    }
    this.committedValue = event.value;
    this.value.set(event.value);
    this.onChange(event.value);
    this.onTouched();
  }

  /** Keeps the form and PrimeNG display on the last valid choice after a locked pick. */
  private revertToCommittedValue(): void {
    this.value.set(this.committedValue);
    this.onChange(this.committedValue);
    queueMicrotask(() => this.select?.writeValue(this.committedValue));
  }

  private emitDisabledOptionClick(option: unknown): void {
    this.disabledOptionClick.emit(option);
  }

  /** PrimeNG item templates may pass the full option or only its `optionValue`. */
  private resolveOption(option: unknown): unknown {
    if (option !== null && typeof option === "object") {
      return option;
    }
    return this.findOptionByValue(option) ?? option;
  }

  private findOptionByValue(value: unknown): unknown | undefined {
    const valueKey = this.optionValue();
    if (valueKey === undefined) {
      return this.options().find(o => o === value);
    }
    return this.options().find(
      o => o !== null && typeof o === "object" && (o as Record<string, unknown>)[valueKey] === value
    );
  }
}
